import { computed, reactive } from "@vue/composition-api";

import {
  AUTHOR_LABEL,
  CREATE_STEPS,
  EDITORIAL_FORMAT_DEAL,
  EDITORIAL_FORMAT_FOCUS,
  SCHEDULE_STEP_MINUTES,
  SIGNATURE_MAX_LENGTH,
  VERIFICATION_STEP,
  WIDGET_MAX_MODELS,
  WIDGET_MAX_PRODUCTS,
} from "./constants";
import {
  affiliationGuidance,
  applyProductEdit,
  clone,
  SIGNATURE_PLAIN_TEXT_MESSAGE,
  dealPriceProblem,
  defaultEditorialFormat,
  isPlainTextSignature,
  mergeOpportunityData,
  selectionKey,
  toProductSnapshot,
  validateProduct,
  validateSourceUrl,
} from "./ctc-model";

const newRequestId = () => {
  if (globalThis.crypto && globalThis.crypto.randomUUID) {
    return globalThis.crypto.randomUUID().replace(/-/g, "");
  }
  return `${Date.now()}_${Math.random().toString(36).slice(2)}`;
};

// Default delay of a new schedule: leaves time for the generation to run.
const SCHEDULE_DEFAULT_DELAY_MINUTES = 30;

const pad = (value) => String(value).padStart(2, "0");

/** Local date and time inputs of `date`, in the browser's time zone. */
const toScheduleInputs = (date) => ({
  scheduledDate: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}`,
  scheduledTime: `${pad(date.getHours())}:${pad(date.getMinutes())}`,
});

/** Products the manager groups by model (tab) and colour for the widget. */
const defaultSimilarSearchTerm = (product) =>
  String((product && product.title) || "").trim();

const emptySimilar = (searchTerm = "") => ({
  selected: [],
  candidates: [],
  searchTerm,
  loading: false,
  error: null,
  variantCandidates: [],
  variantsLoading: false,
  variantsError: null,
});

/**
 * Phase 1 of the current flow. `article` is the verification draft returned
 * by the backend (its id is reused by phase 2 and replaced on a new
 * selection); `key` is the selection it was made for.
 */
const emptyVerification = () => ({
  key: null,
  requestId: null,
  article: null,
  notAffiliated: null,
  running: false,
  rechecking: false,
  resumed: false,
  error: null,
});

const initialState = () => ({
  step: 1,
  sourceUrl: "",
  origin: "manual",
  opportunityId: null,
  opportunityProduct: null,
  analyzing: false,
  analysisUrl: null,
  analysisError: null,
  product: null,
  siteId: null,
  editorialFormat: EDITORIAL_FORMAT_FOCUS,
  checkingOffer: false,
  offerCheck: null,
  offerCheckError: null,
  signature: "",
  postStatus: "draft",
  scheduledDate: null,
  scheduledTime: null,
  widgetEnabled: true,
  widgetType: "BOX",
  similar: emptySimilar(),
  verification: emptyVerification(),
  // The server refused or failed to abandon the verification: nothing changed.
  cancelError: null,
  launching: false,
  launchError: null,
});

/**
 * State of the "Créer un article" stepper. It survives view switches (it is
 * owned by the workspace) and ignores stale async answers through tokens.
 *
 * @param {Object} params
 * @param {Object} params.service - createCtcService() instance
 * @param {Function} [params.onOpportunityDetached] - called with the id of an
 *   opportunity whose source the user replaced by hand
 */
export function useCtcCreateFlow({ service, onOpportunityDetached }) {
  const state = reactive(initialState());
  let analysisToken = 0;
  let offerCheckToken = 0;
  let similarProductsToken = 0;
  let similarVariantsToken = 0;
  const offerChecks = new Map();
  const similarProductSearches = new Map();

  const offerCheckKey = (product, siteId) =>
    product && siteId
      ? `${siteId}:${product.merchant}:${
          product.productLocalizedId || product.merchantProductId || product.id
        }`
      : null;

  const similarProductsKey = (product, siteId, query) =>
    product && siteId
      ? [
          siteId,
          product.merchant || "",
          product.merchantProductId || product.id || "",
          product.productLocalizedId || "",
          product.brand || "",
          product.title || "",
          query,
        ]
          .map((value) => String(value).trim().toLowerCase())
          .join(":")
      : null;

  // =>  COMPUTED
  const urlError = computed(() => {
    const error = validateSourceUrl(state.sourceUrl);
    return error && error.code !== "empty" ? error.message : null;
  });

  const canAnalyze = computed(
    () =>
      state.step === 1 &&
      !state.analyzing &&
      !!state.siteId &&
      !validateSourceUrl(state.sourceUrl)
  );

  const productErrors = computed(() => validateProduct(state.product));

  const isProductValid = computed(
    () => !!state.product && Object.keys(productErrors.value).length === 0
  );

  const signatureError = computed(() => {
    const value = (state.signature || "").trim();
    if (!value) return "La signature de l’article est obligatoire.";
    if (value.length > SIGNATURE_MAX_LENGTH) {
      return `La signature ne peut pas dépasser ${SIGNATURE_MAX_LENGTH} caractères.`;
    }
    if (!isPlainTextSignature(value)) return SIGNATURE_PLAIN_TEXT_MESSAGE;
    return null;
  });

  const hasProduct = computed(() => !!state.product || state.analyzing);

  /** An invalid Bon plan blocks the flow; it is never switched to Focus. */
  const editorialFormatError = computed(() =>
    state.editorialFormat === EDITORIAL_FORMAT_DEAL && state.product
      ? dealPriceProblem(state.product)
      : null
  );

  /** Local date and time inputs read as the browser's time zone. */
  const scheduledAt = computed(() => {
    if (
      state.postStatus !== "future" ||
      !state.scheduledDate ||
      !state.scheduledTime
    ) {
      return null;
    }
    const date = new Date(`${state.scheduledDate}T${state.scheduledTime}:00`);
    return Number.isNaN(date.getTime()) ? null : date;
  });

  const scheduleError = computed(() => {
    if (state.postStatus !== "future") return null;
    if (!scheduledAt.value) {
      return "Indiquez la date et l’heure de publication.";
    }
    if (scheduledAt.value.getTime() <= Date.now()) {
      return "La date de publication doit être dans le futur.";
    }
    return null;
  });

  /** A published or scheduled article goes online without any review. */
  const needsPublishConfirmation = computed(() => state.postStatus !== "draft");

  // Step 1 only reads the offers of the main product; the Affilizz contents
  // are created with the RMR draft at launch.
  const hasMainOffer = computed(
    () =>
      !state.checkingOffer && !!state.offerCheck && state.offerCheck.hasOffer
  );

  const canUseWidget = computed(
    () =>
      !state.widgetEnabled ||
      (state.similar.selected.length > 0 &&
        state.similar.selected.every(
          (group) => group.title.trim().length > 0 && group.title.length <= 255
        ))
  );

  const currentSelectionKey = computed(() =>
    selectionKey({
      siteId: state.siteId,
      product: state.product,
      widgetEnabled: state.widgetEnabled,
      widgetType: state.widgetType,
      similarSelected: state.similar.selected,
    })
  );

  /**
   * The affiliation of the current selection, or null when it was never
   * verified or the product/widget changed since (stale).
   */
  const affiliation = computed(() => {
    const { verification } = state;
    if (!verification.key || verification.key !== currentSelectionKey.value) {
      return null;
    }
    if (verification.notAffiliated) {
      return { ...verification.notAffiliated, overall: "not_affiliated" };
    }
    return (verification.article && verification.article.affiliation) || null;
  });

  const verificationStale = computed(
    () =>
      !!state.verification.key &&
      state.verification.key !== currentSelectionKey.value
  );

  const canVerify = computed(
    () =>
      state.step === VERIFICATION_STEP &&
      isProductValid.value &&
      hasMainOffer.value &&
      canUseWidget.value &&
      !state.verification.running &&
      !state.verification.rechecking &&
      !affiliation.value
  );

  const nextBlockedReason = computed(() => {
    if (state.step === 1) {
      if (!state.siteId) {
        return "Sélectionnez le site de publication avant l'analyse.";
      }
      if (state.analyzing) return "Récupération en cours…";
      if (!state.product) {
        return "Récupérez les informations du produit pour continuer.";
      }
      if (!isProductValid.value) {
        return "Corrigez les données produit signalées pour continuer.";
      }
      if (state.checkingOffer) return "Recherche des offres Affilizz en cours…";
      if (state.offerCheckError) return state.offerCheckError;
      if (!state.offerCheck) {
        return "Recherchez les offres Affilizz du produit pour continuer.";
      }
      if (!state.offerCheck.hasOffer) {
        return (
          state.offerCheck.reason ||
          "Affilizz ne trouve actuellement aucune offre pour ce produit principal."
        );
      }
      return null;
    }
    if (state.step === VERIFICATION_STEP) {
      if (!state.siteId) return "Sélectionnez un site pour continuer.";
      if (state.checkingOffer) return "Recherche des offres Affilizz en cours…";
      if (state.offerCheckError) return state.offerCheckError;
      if (!hasMainOffer.value) {
        return "Aucune offre Affilizz utilisable pour ce produit et ce site. Modifiez le site à l’étape 1.";
      }
      if (state.widgetEnabled && !state.similar.selected.length) {
        return "Choisissez au moins un produit Affilizz ou désactivez le widget.";
      }
      if (
        state.widgetEnabled &&
        state.similar.selected.some((group) => !group.title.trim())
      ) {
        return "Donnez un nom à chaque produit sélectionné.";
      }
      if (
        state.widgetEnabled &&
        state.similar.selected.some((group) => group.title.length > 255)
      ) {
        return "Le nom d’un produit ne peut pas dépasser 255 caractères.";
      }
      if (state.verification.running) {
        return "Vérification de l’affiliation Affilizz en cours…";
      }
      if (!affiliation.value) {
        return "Vérifiez l’affiliation Affilizz pour continuer.";
      }
      // Same source as the step's cards and actions.
      const guidance = affiliationGuidance(affiliation.value);
      if (!guidance.canContinue) return guidance.title;
    }
    if (state.step === VERIFICATION_STEP + 1) {
      if (editorialFormatError.value) return editorialFormatError.value;
      if (signatureError.value) return signatureError.value;
      if (scheduleError.value) return scheduleError.value;
    }
    return null;
  });

  const canGoNext = computed(
    () => state.step < CREATE_STEPS.length && !nextBlockedReason.value
  );

  /**
   * First condition of canLaunch still missing, with the step that fixes it,
   * for the final control; null when nothing blocks the launch.
   */
  const launchBlocker = computed(() => {
    if (!state.siteId) {
      return { step: 1, message: "Sélectionnez le site de publication." };
    }
    if (!isProductValid.value) {
      return { step: 1, message: "Corrigez les données produit signalées." };
    }
    if (!affiliation.value) {
      return {
        step: VERIFICATION_STEP,
        message: "Vérifiez l’affiliation Affilizz de cette sélection.",
      };
    }
    const guidance = affiliationGuidance(affiliation.value);
    if (!guidance.canContinue) {
      return { step: VERIFICATION_STEP, message: guidance.title };
    }
    const settingsError =
      editorialFormatError.value || signatureError.value || scheduleError.value;
    if (settingsError) {
      return { step: VERIFICATION_STEP + 1, message: settingsError };
    }
    return null;
  });

  const canLaunch = computed(
    () =>
      state.step === CREATE_STEPS.length &&
      !!state.siteId &&
      isProductValid.value &&
      !editorialFormatError.value &&
      !signatureError.value &&
      !scheduleError.value &&
      !!affiliation.value &&
      affiliationGuidance(affiliation.value).canContinue &&
      !state.launching
  );

  // =>  METHODS
  const resetSimilar = (searchTerm = "") => {
    similarProductsToken += 1;
    similarVariantsToken += 1;
    state.similar = emptySimilar(searchTerm);
  };

  /**
   * A verification not finalized is abandoned on the server first: its
   * opportunity is released (the Affilizz contents stay orphaned). Resolves
   * false, leaving every local state untouched, when the server refused or
   * failed; concurrent callers share the same request.
   */
  let pendingCancellation = null;
  const cancelPendingVerification = () => {
    const { article, requestId } = state.verification;
    if (!article || article.phase !== "verification" || !requestId) {
      state.verification = emptyVerification();
      return Promise.resolve(true);
    }
    if (!pendingCancellation) {
      state.cancelError = null;
      pendingCancellation = service
        .cancelAffilizzCheck(requestId)
        .then(() => {
          if (state.verification.requestId === requestId) {
            state.verification = emptyVerification();
          }
          return true;
        })
        .catch((error) => {
          state.cancelError = `${
            error.message ||
            "La vérification Affilizz n’a pas pu être abandonnée."
          } Rien n’a été modifié : réessayez.`;
          return false;
        })
        .finally(() => {
          pendingCancellation = null;
        });
    }
    return pendingCancellation;
  };

  /** Resolves false, and clears nothing, if the verification can not be abandoned. */
  const clearAnalysis = async () => {
    if (!(await cancelPendingVerification())) return false;
    analysisToken += 1;
    offerCheckToken += 1;
    cancelPendingVerification();
    resetSimilar();
    Object.assign(state, {
      analyzing: false,
      analysisUrl: null,
      analysisError: null,
      launchError: null,
      product: null,
      checkingOffer: false,
      offerCheck: null,
      offerCheckError: null,
    });
    return true;
  };

  const setSourceUrl = async (value) => {
    state.sourceUrl = value || "";
    const url = state.sourceUrl.trim();
    // A new source invalidates everything derived from the previous one.
    const invalidates = state.analysisUrl !== null && url !== state.analysisUrl;
    if (invalidates && !(await cancelPendingVerification())) {
      // The verified product stays: so does the source it was analysed from.
      state.sourceUrl = state.analysisUrl;
      return;
    }

    if (
      state.origin === "opportunity" &&
      state.opportunityProduct &&
      url !== state.opportunityProduct.sourceUrl
    ) {
      const detachedId = state.opportunityId;
      Object.assign(state, {
        origin: "manual",
        opportunityId: null,
        opportunityProduct: null,
      });
      if (onOpportunityDetached) onOpportunityDetached(detachedId);
    }

    if (invalidates) await clearAnalysis();
  };

  const analyze = async () => {
    if (!canAnalyze.value) return;
    if (!(await clearAnalysis())) return;
    const token = analysisToken;
    const url = state.sourceUrl.trim();
    state.analyzing = true;
    state.analysisUrl = url;

    try {
      const product = await service.analyzeSource(url, state.siteId);
      if (token !== analysisToken) return;
      state.product =
        state.origin === "opportunity"
          ? mergeOpportunityData(product, state.opportunityProduct)
          : product;
      state.similar.searchTerm = defaultSimilarSearchTerm(state.product);
      checkOffer();
    } catch (error) {
      if (token !== analysisToken) return;
      state.analysisError = {
        code: error.code || "analysis_failed",
        message: error.message || "L’analyse de la fiche produit a échoué.",
      };
    } finally {
      if (token === analysisToken) state.analyzing = false;
    }
  };

  const updateProductField = (field, value) => {
    const previousTitle = state.product && state.product.title;
    state.product = applyProductEdit(state.product, field, value);
    if (field === "title" && state.product.title !== previousTitle) {
      resetSimilar(defaultSimilarSearchTerm(state.product));
    }
  };

  const checkOffer = async (force = false) => {
    offerCheckToken += 1;
    const token = offerCheckToken;
    state.checkingOffer = false;
    state.offerCheckError = null;
    if (!state.product || !state.siteId) return;

    const { product, siteId } = state;
    const key = offerCheckKey(product, siteId);
    if (
      !force &&
      state.offerCheck &&
      state.offerCheck.productId === product.id &&
      state.offerCheck.siteId === siteId &&
      state.offerCheck.hasOffer
    ) {
      return;
    }
    if (!force && offerChecks.has(key)) {
      state.offerCheck = clone(offerChecks.get(key));
      return;
    }
    state.offerCheck = null;
    state.checkingOffer = true;

    try {
      const offerCheck = await service.checkOffer({ product, siteId });
      // The user may have picked another site meanwhile.
      if (token !== offerCheckToken) return;
      offerChecks.set(key, clone(offerCheck));
      state.offerCheck = offerCheck;
    } catch (error) {
      if (token !== offerCheckToken) return;
      state.offerCheckError =
        error.message || "La recherche des offres Affilizz a échoué.";
    } finally {
      if (token === offerCheckToken) state.checkingOffer = false;
    }
  };

  const loadSimilarProducts = async () => {
    similarProductsToken += 1;
    const token = similarProductsToken;
    state.similar.loading = false;
    state.similar.error = null;
    if (!state.widgetEnabled || !state.product || !state.siteId) return;

    const { product, siteId } = state;
    const query = state.similar.searchTerm.trim();
    if (query && query.length < 2) {
      state.similar.error =
        "Saisissez au moins deux caractères pour rechercher.";
      return;
    }
    const key = similarProductsKey(product, siteId, query);
    if (similarProductSearches.has(key)) {
      state.similar.candidates = clone(similarProductSearches.get(key));
      return;
    }

    state.similar.candidates = [];
    state.similar.loading = true;
    try {
      const products = await service.findSimilarProducts({
        product,
        siteId,
        query,
      });
      if (token !== similarProductsToken) return;
      similarProductSearches.set(key, clone(products));
      state.similar.candidates = products;
    } catch (error) {
      if (token !== similarProductsToken) return;
      state.similar.error =
        error.message || "La recherche de produits similaires a échoué.";
    } finally {
      if (token === similarProductsToken) {
        state.similar.loading = false;
      }
    }
  };

  const loadSimilarVariants = async (query) => {
    similarVariantsToken += 1;
    const token = similarVariantsToken;
    state.similar.variantCandidates = [];
    state.similar.variantsLoading = false;
    state.similar.variantsError = null;
    if (!state.widgetEnabled || !state.product || !state.siteId) return;

    const normalizedQuery = String(query || "").trim();
    if (normalizedQuery.length < 2) {
      state.similar.variantsError =
        "Saisissez au moins deux caractères pour rechercher un coloris.";
      return;
    }
    const { product, siteId } = state;
    const key = similarProductsKey(product, siteId, normalizedQuery);
    if (similarProductSearches.has(key)) {
      state.similar.variantCandidates = clone(similarProductSearches.get(key));
      return;
    }

    state.similar.variantsLoading = true;
    try {
      const products = await service.findSimilarProducts({
        product,
        siteId,
        query: normalizedQuery,
      });
      if (token !== similarVariantsToken) return;
      similarProductSearches.set(key, clone(products));
      state.similar.variantCandidates = products;
    } catch (error) {
      if (token !== similarVariantsToken) return;
      state.similar.variantsError =
        error.message || "La recherche de coloris Affilizz a échoué.";
    } finally {
      if (token === similarVariantsToken) {
        state.similar.variantsLoading = false;
      }
    }
  };

  const selectSite = async (siteId, defaultSignature = AUTHOR_LABEL) => {
    if (state.step !== 1) return;
    const selectedSiteId = siteId || null;
    if (selectedSiteId === state.siteId) return;
    // The site decides the Affilizz media: its verification goes first.
    if (!(await cancelPendingVerification())) return;

    state.siteId = selectedSiteId;
    state.signature = selectedSiteId ? defaultSignature || AUTHOR_LABEL : "";
    if (state.origin === "opportunity") {
      offerCheckToken += 1;
      resetSimilar(defaultSimilarSearchTerm(state.product));
      Object.assign(state, {
        checkingOffer: false,
        offerCheck: null,
        offerCheckError: null,
      });
      checkOffer();
      return;
    }
    await clearAnalysis();
  };

  /**
   * Drops the widget from a verified selection while keeping its CTA: the
   * server keeps the same verification and contents, nothing is recreated.
   */
  const disableVerificationWidget = async () => {
    const { article } = state.verification;
    if (!article || state.verification.rechecking) return;
    state.verification.rechecking = true;
    state.verification.error = null;
    try {
      const updated = await service.disableAffilizzWidget(article.id);
      state.widgetEnabled = false;
      resetSimilar();
      state.verification.article = updated;
      state.verification.key = currentSelectionKey.value;
    } catch (error) {
      state.verification.error =
        error.message || "Le widget n’a pas pu être retiré.";
    } finally {
      state.verification.rechecking = false;
    }
  };

  const setWidgetEnabled = (value) => {
    if (
      !value &&
      state.verification.article &&
      state.verification.article.phase === "verification" &&
      !verificationStale.value
    ) {
      disableVerificationWidget();
      return;
    }
    state.widgetEnabled = !!value;
    if (state.widgetEnabled && !state.similar.candidates.length) {
      if (!state.similar.searchTerm.trim()) {
        state.similar.searchTerm = defaultSimilarSearchTerm(state.product);
      }
      loadSimilarProducts();
    }
  };

  const setSimilarSearchTerm = (value) => {
    state.similar.searchTerm = value || "";
  };

  const addSimilarProduct = ({ product, groupIndex = null }) => {
    if (
      !product ||
      !product.productLocalizedId ||
      product.offerCount < 1 ||
      state.similar.selected.some((group) =>
        group.products.some(
          (item) => item.productLocalizedId === product.productLocalizedId
        )
      )
    ) {
      return;
    }
    const selectedCount = state.similar.selected.reduce(
      (count, group) => count + group.products.length,
      0
    );
    if (selectedCount >= WIDGET_MAX_PRODUCTS) return;
    if (groupIndex === null) {
      if (state.similar.selected.length >= WIDGET_MAX_MODELS) return;
      state.similar.selected.push({
        title: product.title.slice(0, 255),
        products: [clone(product)],
      });
    } else if (state.similar.selected[groupIndex]) {
      state.similar.selected[groupIndex].products.push(clone(product));
    }
  };

  const removeSimilarProduct = (productId) => {
    state.similar.selected = state.similar.selected
      .map((group) => ({
        ...group,
        products: group.products.filter(
          (product) => product.productLocalizedId !== productId
        ),
      }))
      .filter((group) => group.products.length);
  };

  const renameSimilarGroup = ({ index, title }) => {
    if (state.similar.selected[index]) {
      state.similar.selected[index].title = title;
    }
  };

  const setWidgetType = (value) => {
    state.widgetType = value === "CARD" ? "CARD" : "BOX";
  };

  const setEditorialFormat = (value) => {
    state.editorialFormat =
      value === EDITORIAL_FORMAT_DEAL
        ? EDITORIAL_FORMAT_DEAL
        : EDITORIAL_FORMAT_FOCUS;
  };

  const setSignature = (value) => {
    state.signature = value || "";
  };

  /**
   * "draft", "publish" or "future" — the same three CMS post statuses as the
   * other AI Factory flows. Choosing "future" proposes the next free slot.
   */
  const setPostStatus = (value) => {
    state.postStatus = ["publish", "future"].includes(value) ? value : "draft";
    if (state.postStatus !== "future") {
      Object.assign(state, { scheduledDate: null, scheduledTime: null });
      return;
    }
    if (!state.scheduledDate || !state.scheduledTime) {
      const date = new Date();
      date.setMinutes(
        Math.ceil(
          (date.getMinutes() + SCHEDULE_DEFAULT_DELAY_MINUTES) /
            SCHEDULE_STEP_MINUTES
        ) * SCHEDULE_STEP_MINUTES,
        0,
        0
      );
      Object.assign(state, toScheduleInputs(date));
    }
  };

  const setSchedule = ({ date, time } = {}) => {
    if (date !== undefined) state.scheduledDate = date || null;
    if (time !== undefined) state.scheduledTime = time || null;
  };

  const selection = () => ({
    product: clone(state.product),
    siteId: state.siteId,
    productSnapshot: toProductSnapshot(state.product),
    widgetEnabled: state.widgetEnabled,
    widgetType: state.widgetType,
    similarProducts: state.widgetEnabled ? clone(state.similar.selected) : [],
    opportunityId: state.opportunityId,
  });

  /**
   * Phase 1. The same selection keeps its request id (a repeated call returns
   * the same verification); a new selection gets a new one and replaces the
   * previous verification draft.
   */
  const verifyAffilizz = async () => {
    if (!canVerify.value) return;
    const key = currentSelectionKey.value;
    const previous = state.verification;
    const sameSelection = previous.key === key && !!previous.requestId;
    const replaced =
      !sameSelection && previous.article ? previous.requestId : null;
    state.verification = {
      ...emptyVerification(),
      key,
      requestId: sameSelection ? previous.requestId : newRequestId(),
      article: sameSelection ? previous.article : null,
      running: true,
    };
    try {
      const article = await service.verifyAffilizz({
        ...selection(),
        requestId: state.verification.requestId,
        replacesRequestId: replaced,
      });
      if (state.verification.key === key) state.verification.article = article;
    } catch (error) {
      if (state.verification.key !== key) return;
      if (error.code === "affilizz_not_affiliated") {
        // Nothing was kept: a new attempt needs another product or widget.
        state.verification.article = null;
        state.verification.notAffiliated = (error.details &&
          error.details.affiliation) || {
          link: false,
          widget: null,
          widgetIncluded: state.widgetEnabled,
        };
      } else {
        state.verification.error =
          error.message || "La vérification Affilizz a échoué.";
      }
    } finally {
      if (state.verification.key === key) state.verification.running = false;
    }
  };

  /** Reads the same contents again while their affiliation is unknown. */
  const recheckAffiliation = async () => {
    const { article, key } = state.verification;
    if (!article || state.verification.rechecking) return;
    state.verification.rechecking = true;
    state.verification.error = null;
    try {
      const updated = await service.recheckAffiliation(article.id);
      if (state.verification.key === key) state.verification.article = updated;
    } catch (error) {
      if (state.verification.key !== key) return;
      if (error.code === "affilizz_not_affiliated") {
        state.verification.article = null;
        state.verification.notAffiliated = (error.details &&
          error.details.affiliation) || {
          link: false,
          widget: null,
          widgetIncluded: state.widgetEnabled,
        };
      } else {
        state.verification.error =
          error.message || "La vérification de l’affiliation a échoué.";
      }
    } finally {
      state.verification.rechecking = false;
    }
  };

  const next = () => {
    if (!canGoNext.value) return;
    state.step += 1;
    if (
      state.step === VERIFICATION_STEP &&
      state.widgetEnabled &&
      !state.similar.candidates.length &&
      !state.similar.loading
    ) {
      if (!state.similar.searchTerm.trim()) {
        state.similar.searchTerm = defaultSimilarSearchTerm(state.product);
      }
      loadSimilarProducts();
    }
  };

  const previous = () => {
    if (state.step > 1) state.step -= 1;
  };

  const goToStep = (step) => {
    const target = Number(step);
    if (Number.isInteger(target) && target >= 1 && target <= state.step) {
      state.step = target;
    }
  };

  const clearLocalFlow = () => {
    analysisToken += 1;
    offerCheckToken += 1;
    similarProductsToken += 1;
    similarVariantsToken += 1;
    Object.assign(state, initialState());
  };

  /** Resolves false, keeping the whole flow, if the verification can not be abandoned. */
  const reset = async () => {
    if (!(await cancelPendingVerification())) return false;
    clearLocalFlow();
    return true;
  };

  /** Leaves a resumed verification locally; the server draft stays untouched. */
  const leaveResumedVerification = () => {
    if (!state.verification.resumed || state.launching) return false;
    clearLocalFlow();
    return true;
  };

  /** Clears a verification locally after its server cancellation succeeded. */
  const clearAbandonedVerification = (requestId) => {
    if (
      !requestId ||
      requestId !== state.verification.requestId ||
      state.launching
    ) {
      return false;
    }
    clearLocalFlow();
    return true;
  };

  /**
   * Resumes a pending verification after a reload: the same draft, request
   * id and Affilizz contents, on the widget and affiliation step.
   */
  const resumeVerification = async (
    article,
    defaultSignature = AUTHOR_LABEL
  ) => {
    if (!(await reset())) return false;
    Object.assign(state, {
      siteId: article.siteId,
      product: clone(article.product),
      sourceUrl: article.product.sourceUrl || "",
      analysisUrl: article.product.sourceUrl || "",
      signature: defaultSignature || AUTHOR_LABEL,
      widgetEnabled: article.widgetEnabled,
      widgetType: article.widgetType,
      similar: {
        ...emptySimilar(defaultSimilarSearchTerm(article.product)),
        selected: clone(article.similarProducts),
      },
      step: VERIFICATION_STEP,
    });
    state.verification = {
      ...emptyVerification(),
      key: currentSelectionKey.value,
      requestId: article.requestId,
      article: clone(article),
      resumed: true,
    };
    checkOffer();
    if (state.widgetEnabled) loadSimilarProducts();
    return true;
  };

  /** Resolves null, keeping the current flow, if its verification can not be abandoned. */
  const loadOpportunity = async (opportunity) => {
    if (!(await reset())) return null;
    Object.assign(state, {
      origin: "opportunity",
      opportunityId: opportunity.id,
      opportunityProduct: clone(opportunity.product),
      sourceUrl: opportunity.product.sourceUrl || "",
      analysisUrl: opportunity.product.sourceUrl || "",
      product: clone(opportunity.product),
      editorialFormat: defaultEditorialFormat(opportunity.product, true),
    });
    state.similar = emptySimilar(defaultSimilarSearchTerm(state.product));
    return state.product;
  };

  /** Phase 2: final product facts and editorial fields of the verified draft. */
  const buildFinalizeRequest = () => ({
    title: state.product.title,
    productSnapshot: {
      ...toProductSnapshot(state.product),
      editorialFormat: state.editorialFormat,
    },
    signature: state.signature.trim(),
    postStatus: state.postStatus,
    scheduledAt: scheduledAt.value ? scheduledAt.value.toISOString() : null,
  });

  const dispose = () => {
    analysisToken += 1;
    offerCheckToken += 1;
    similarProductsToken += 1;
    similarVariantsToken += 1;
  };

  return {
    state,
    urlError,
    canAnalyze,
    productErrors,
    editorialFormatError,
    signatureError,
    isProductValid,
    hasProduct,
    affiliation,
    verificationStale,
    canVerify,
    verifyAffilizz,
    recheckAffiliation,
    disableVerificationWidget,
    resumeVerification,
    leaveResumedVerification,
    clearAbandonedVerification,
    nextBlockedReason,
    canGoNext,
    canLaunch,
    launchBlocker,
    canUseWidget,
    needsPublishConfirmation,
    scheduledAt,
    scheduleError,
    setSourceUrl,
    analyze,
    updateProductField,
    selectSite,
    retryOfferCheck: () => checkOffer(true),
    similarActions: {
      search: loadSimilarProducts,
      searchVariants: loadSimilarVariants,
      setSearchTerm: setSimilarSearchTerm,
      add: addSimilarProduct,
      remove: removeSimilarProduct,
      renameGroup: renameSimilarGroup,
    },
    setEditorialFormat,
    setSignature,
    setPostStatus,
    setSchedule,
    setWidgetEnabled,
    setWidgetType,
    goToStep,
    next,
    previous,
    reset,
    loadOpportunity,
    buildFinalizeRequest,
    dispose,
  };
}
